import { useState } from 'react';
import { formatClientFacingPrice } from '@/lib/changeOrderPricingDisplay';
import ClientLayout from '@/components/feature/ClientLayout';
import { useClientProject } from '@/hooks/useClientProject';
import { useUnifiedInstructions } from '@/hooks/useUnifiedInstructions';
import { useTranslation } from 'react-i18next';

function fmtDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function ClientInstructions() {
  const { t } = useTranslation();
  const { selectedProject, projectId } = useClientProject();
  const { items, loading, submitResponse, pendingCount } = useUnifiedInstructions(projectId);
  const locale = t('date.locale') || 'en-NZ';

  function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('common.justNow');
    if (mins < 60) return t('common.minAgo', { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('common.hrAgo', { count: hrs, s: hrs !== 1 ? 's' : '' });
    const days = Math.floor(hrs / 24);
    return t('common.dayAgo', { count: days, s: days !== 1 ? 's' : '' });
  }

  function getTypeConfig(type: string | null) {
    const configs: Record<string, { label: string; icon: string; color: string; bg: string }> = {
      order_change: { label: t('client.type.orderChange'), icon: 'ri-file-edit-line', color: '#E8621A', bg: '#FEF0E7' },
      variation: { label: t('client.type.variation'), icon: 'ri-git-pull-request-line', color: '#0891B2', bg: '#ECFEFF' },
      approval_request: { label: t('client.type.approvalRequired'), icon: 'ri-shield-check-line', color: '#059669', bg: '#F0FDF4' },
      general_instruction: { label: t('client.type.instruction'), icon: 'ri-file-list-3-line', color: '#374151', bg: '#F9FAFB' },
      design_decision: { label: t('client.type.designDecision'), icon: 'ri-palette-line', color: '#7C3AED', bg: '#F5F3FF' },
      payment_related: { label: t('client.type.payment'), icon: 'ri-bank-card-line', color: '#059669', bg: '#F0FDF4' },
      schedule_change: { label: t('client.type.scheduleChange'), icon: 'ri-calendar-line', color: '#F59E0B', bg: '#FFFBEB' },
      change_order: { label: t('client.type.changeOrder'), icon: 'ri-exchange-line', color: '#E8621A', bg: '#FEF0E7' },
      instruction: { label: t('client.type.instruction'), icon: 'ri-file-list-3-line', color: '#374151', bg: '#F9FAFB' },
    };
    return configs[type?.toLowerCase() ?? ''] || configs.general_instruction;
  }

  function getStatusConfig(status: string) {
    const configs: Record<string, { label: string; color: string; bg: string; icon: string }> = {
      pending: { label: t('client.status.pending'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-time-line' },
      sent: { label: t('client.status.sent'), color: '#0891B2', bg: '#ECFEFF', icon: 'ri-send-plane-line' },
      in_progress: { label: t('client.status.inProgress'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-loader-2-line' },
      approved: { label: t('client.status.approved'), color: '#059669', bg: '#F0FDF4', icon: 'ri-check-line' },
      declined: { label: t('client.status.declined'), color: '#EF4444', bg: '#FEF2F2', icon: 'ri-close-line' },
      client_approved: { label: t('client.status.clientApproved'), color: '#059669', bg: '#F0FDF4', icon: 'ri-check-line' },
      client_rejected: { label: t('client.status.clientDeclined'), color: '#EF4444', bg: '#FEF2F2', icon: 'ri-close-line' },
      acknowledged: { label: t('client.status.acknowledged'), color: '#0891B2', bg: '#ECFEFF', icon: 'ri-eye-line' },
      closed: { label: t('client.status.closed'), color: '#6B7280', bg: '#F9FAFB', icon: 'ri-lock-line' },
      completed: { label: t('client.status.completed'), color: '#059669', bg: '#F0FDF4', icon: 'ri-check-double-line' },
      sent_to_client: { label: t('client.status.awaitingApproval'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-time-line' },
      pending_client_approval: { label: t('client.status.awaitingApproval'), color: '#E8621A', bg: '#FEF0E7', icon: 'ri-time-line' },
    };
    return configs[status?.toLowerCase() ?? ''] || configs.pending;
  }

  function isPendingStatus(status: string): boolean {
    const s = status.toLowerCase();
    return s === 'pending' || s === 'sent' || s === 'sent_to_client' || s === 'pending_client_approval' || s === 'in_progress';
  }

  const [actionId, setActionId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'decline' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(id: string, action: 'approved' | 'declined') {
    setSubmitting(true);
    const ok = await submitResponse(id, action, responseText);
    if (ok) {
      setActionId(null);
      setActionType(null);
      setResponseText('');
    }
    setSubmitting(false);
  }

  return (
    <ClientLayout title={t('client.instructions.title')}>
      <div className="px-4 lg:px-8 pt-5 pb-10">
        {selectedProject && (
          <div className="mb-5 flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-[#E8621A] flex items-center justify-center flex-shrink-0">
              <i className="ri-building-2-line text-white text-[10px]"></i>
            </div>
            <div>
              <span className="text-[#1A1A1A] text-sm font-bold">{selectedProject.project_name}</span>
              {selectedProject.site_address && (
                <span className="text-[#9CA3AF] text-xs ml-2">{selectedProject.site_address}</span>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-[15px] font-bold text-[#1A1A1A]">{t('client.instructions.instructionsDecisions')}</h2>
            <p className="text-[#9CA3AF] text-xs mt-0.5">{t('client.instructions.instructionsDesc')}</p>
          </div>
          {pendingCount > 0 && (
            <span className="bg-[#E8621A] text-white text-xs font-bold px-3 py-1 rounded-full">
              {t('client.instructions.pending', { count: pendingCount })}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-file-list-3-line text-5xl"></i>
            </div>
            <p className="text-[#374151] font-semibold text-sm">{t('client.instructions.noInstructions')}</p>
            <p className="text-[#9CA3AF] text-xs mt-1 max-w-xs mx-auto">{t('client.instructions.noInstructionsDesc')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((inst) => {
              const typeCfg = getTypeConfig(inst.type);
              const statusCfg = getStatusConfig(inst.status);
              const isPendingApproval = inst.requires_client_approval && isPendingStatus(inst.status);
              const isActionOpen = actionId === inst.id;

              return (
                <div
                  key={`${inst.source_table}-${inst.id}`}
                  className={`bg-white rounded-2xl border overflow-hidden ${isPendingApproval ? 'border-[#E8621A]/20' : 'border-gray-100'}`}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {inst.co_number && (
                        <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-lg tracking-wide flex-shrink-0">
                          {inst.co_number}
                        </span>
                      )}
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ backgroundColor: typeCfg.bg }}>
                        <i className={`${typeCfg.icon} text-xs`} style={{ color: typeCfg.color }}></i>
                        <span className="text-[10px] font-bold" style={{ color: typeCfg.color }}>{typeCfg.label}</span>
                      </div>

                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: statusCfg.bg }}>
                        <i className={`${statusCfg.icon} text-[10px]`} style={{ color: statusCfg.color }}></i>
                        <span className="text-[10px] font-bold" style={{ color: statusCfg.color }}>{statusCfg.label}</span>
                      </div>

                      <span className="text-[#D1D5DB] text-[10px] ml-auto">{timeAgo(inst.created_at)}</span>
                    </div>

                    <h4 className="text-[#1A1A1A] text-[15px] font-bold leading-snug mb-2">{inst.title}</h4>

                    {inst.reason && (
                      <p className="text-[#6B7280] text-sm leading-relaxed mb-2">
                        <span className="font-semibold">{t('client.instructions.reason')}</span> {inst.reason}
                      </p>
                    )}

                    {inst.body && (
                      <p className="text-[#6B7280] text-sm leading-relaxed whitespace-pre-line">{inst.body}</p>
                    )}

                    {(inst.current_finish_description || inst.new_finish_description) && (
                      <div className="mt-2 space-y-1">
                        {inst.current_finish_description && (
                          <p className="text-xs text-[#374151]">
                            <span className="font-semibold">{t('client.instructions.currentFinish')}</span> {inst.current_finish_description}
                          </p>
                        )}
                        {inst.new_finish_description && (
                          <p className="text-xs text-[#374151]">
                            <span className="font-semibold">{t('client.instructions.newFinish')}</span> {inst.new_finish_description}
                          </p>
                        )}
                      </div>
                    )}

                    {(inst.client_price != null || inst.schedule_impact) && (
                      <div className="mt-3 flex gap-3 flex-wrap">
                        {inst.client_price != null && (
                          <span className="text-xs text-[#374151] bg-[#F9FAFB] px-2.5 py-1 rounded-lg border border-gray-100">
                            <i className="ri-money-dollar-circle-line text-[10px] mr-1"></i>
                            {(() => { const price = formatClientFacingPrice(inst.client_price, { title: inst.title, reason: inst.reason, type: inst.type }); return (<>{price.isCredit ? 'Client Credit' : t('client.instructions.price')} <span className="font-semibold">{price.amountText}</span></>); })()}
                          </span>
                        )}
                        {inst.schedule_impact && (
                          <span className="text-xs text-[#374151] bg-[#F9FAFB] px-2.5 py-1 rounded-lg border border-gray-100">
                            <i className="ri-calendar-line text-[10px] mr-1"></i>
                            {t('client.instructions.schedule')} <span className="font-semibold">{inst.schedule_impact}</span>
                          </span>
                        )}
                      </div>
                    )}

                    {inst.client_response && (
                      <div className="mt-3 bg-[#F9FAFB] rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <i className="ri-user-line text-[10px] text-[#374151]"></i>
                          <span className="text-[10px] font-bold text-[#374151]">{t('client.instructions.yourResponse')}</span>
                          {inst.client_responded_at && (
                            <span className="text-[#D1D5DB] text-[10px]">{fmtDate(inst.client_responded_at, locale)}</span>
                          )}
                        </div>
                        <p className="text-[#374151] text-sm">{inst.client_response}</p>
                      </div>
                    )}

                    {isPendingApproval && (
                      <div className="mt-4">
                        {!isActionOpen ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setActionId(inst.id); setActionType('approve'); setResponseText(''); }}
                              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#F0FDF4] text-[#059669] text-sm font-semibold cursor-pointer hover:bg-[#DCFCE7] transition-colors whitespace-nowrap border border-[#BBF7D0]"
                            >
                              <i className="ri-check-line"></i>
                              {t('client.instructions.approve')}
                            </button>
                            <button
                              onClick={() => { setActionId(inst.id); setActionType('decline'); setResponseText(''); }}
                              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#FEF2F2] text-[#EF4444] text-sm font-semibold cursor-pointer hover:bg-[#FEE2E2] transition-colors whitespace-nowrap border border-[#FECACA]"
                            >
                              <i className="ri-close-line"></i>
                              {t('client.instructions.decline')}
                            </button>
                          </div>
                        ) : (
                          <div className="bg-[#F9FAFB] rounded-xl p-4 space-y-3">
                            <p className="text-[#1A1A1A] text-sm font-semibold">
                              {actionType === 'approve' ? t('client.instructions.approveChangeOrder') : t('client.instructions.declineChangeOrder')}
                            </p>
                            <textarea
                              value={responseText}
                              onChange={(e) => setResponseText(e.target.value.slice(0, 500))}
                              placeholder={t('client.instructions.optionalComment')}
                              rows={2}
                              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
                            />
                            <div className="flex items-center justify-between">
                              <span className="text-[#D1D5DB] text-[10px]">{responseText.length}/500</span>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => { setActionId(null); setActionType(null); setResponseText(''); }}
                                  className="px-3 py-2 rounded-lg border border-gray-200 text-[#374151] text-xs cursor-pointer whitespace-nowrap"
                                >
                                  {t('client.instructions.cancel')}
                                </button>
                                <button
                                  onClick={() => handleSubmit(inst.id, actionType === 'approve' ? 'approved' : 'declined')}
                                  disabled={submitting}
                                  className={`px-3 py-2 rounded-lg text-white text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-40 ${actionType === 'approve' ? 'bg-[#059669]' : 'bg-[#EF4444]'}`}
                                >
                                  {submitting ? (
                                    <i className="ri-loader-4-line animate-spin"></i>
                                  ) : actionType === 'approve' ? (
                                    t('client.instructions.confirmApproval')
                                  ) : (
                                    t('client.instructions.confirmDecline')
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}